// src/api/axios.js
import axios from "axios";

// 1) Résolution baseURL
let ENV_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    (process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL)) ||
  null;

if (ENV_BASE && !/^https?:\/\//i.test(ENV_BASE)) {
  const proto = typeof window !== "undefined" ? window.location?.protocol || "https:" : "https:";
  ENV_BASE = `${proto}//${ENV_BASE}`;
}

const API_BASE = ENV_BASE || "/api"; // same-origin proxy

if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line no-console
  console.debug("[axios] API baseURL =", API_BASE);
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
});

api.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
api.defaults.headers.common["Accept"] = "application/json, text/plain, */*";

// Utils cookies
function getCookieAll(name) {
  const cookies = document.cookie ? document.cookie.split(";") : [];
  return cookies
    .map((c) => c.trim())
    .filter((c) => c.startsWith(name + "="))
    .map((c) => decodeURIComponent(c.split("=")[1]));
}
function getLatestCookie(name) {
  const all = getCookieAll(name);
  if (!all.length) return null;
  return all[all.length - 1];
}
const NEEDS_CSRF = new Set(["post", "put", "patch", "delete"]);

// 👉 Intercepteur requête
api.interceptors.request.use(
  (config) => {
    // ⚠️ Ne PAS écraser un X-Tenant explicitement fourni par l'appelant
    const alreadyHasTenant =
      (config.headers && (config.headers["X-Tenant"] || config.headers["x-tenant"])) || false;

    if (!alreadyHasTenant) {
      try {
        const company = localStorage.getItem("company");
        if (company) {
          config.headers = config.headers || {};
          config.headers["X-Tenant"] = company;
        }
      } catch {
        /* no-op */
      }
    }

    // CSRF sur mutations
    const method = (config.method || "get").toLowerCase();
    if (NEEDS_CSRF.has(method)) {
      const token = getLatestCookie("csrftoken");
      if (token) {
        config.headers = config.headers || {};
        config.headers["X-CSRFToken"] = token;
        config.headers["X-Csrftoken"] = token;
        config.headers["x-csrftoken"] = token;
      }
    }

    if (!config.headers["Content-Type"] && config.data != null) {
      config.headers["Content-Type"] = "application/json";
    }

    if (process.env.NODE_ENV !== "production" && NEEDS_CSRF.has(method)) {
      // eslint-disable-next-line no-console
      console.debug(
        "[axios] CSRF:",
        config.headers["X-CSRFToken"] || config.headers["X-Csrftoken"] || "(absent)"
      );
      // eslint-disable-next-line no-console
      console.debug("[axios] X-Tenant:", config.headers["X-Tenant"] || "(absent)");
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Réponses (logs légers)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isMe401 = status === 401 && url.includes("/users/me/");
    const isDuringLogin = Boolean(window.__AUTH_LOGIN_FLOW__);
    if (process.env.NODE_ENV !== "production") {
      if (!isMe401 && !isDuringLogin) {
        // eslint-disable-next-line no-console
        console.error("[axios] error:", status, error?.response?.data || error?.message);
      } else {
        // eslint-disable-next-line no-console
        console.debug("[axios] (silenced) 401:", url, error?.response?.data || error?.message);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE };
